import { useEffect, useState } from 'react';

import { actions } from '../models';
import { useAppDispatch } from '../store';
import type { NomineeType, PollType } from '../types';
import LocalStorageClient from '../utils/LocalStorageClient';

import { useCurrentSlide } from './useCurrentSlide';
import { useOptions } from './useOptions';
import { usePollStatus } from './usePollStatus';
import { useUser } from './useUser';

/**
 * Custom hook for managing the state and behavior of the vote "like" button.
 *
 * @param {PollType} poll - The poll object.
 * @param {NomineeType} nominee - The nominee object.
 * @returns {Object} The state and behavior of the "like" button.
 */
export const useLikeButtonState = (poll: PollType, nominee: NomineeType) => {
  const { user, isIdpReady, isAuthenticated } = useUser();
  const dispatch = useAppDispatch();
  const isCurrentSlide = useCurrentSlide();

  const [isVoted, setIsVoted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isButtonHidden, setIsButtonHidden] = useState(true);
  const { pollStatus } = useOptions();
  const { pollIsOpen, pollIsClosed, pollIsWinner } = usePollStatus(poll, pollStatus);

  useEffect(() => {
    const nomineeVoted = LocalStorageClient.nomineeIsVoted(poll, nominee);
    setIsVoted(nomineeVoted);
    // enabled only when poll is open, user hasn't voted, and (if in a carousel) slide is active
    const buttonShouldBeEnabled = pollIsOpen && !user?.alreadyVoted && isCurrentSlide;
    setIsButtonDisabled(!buttonShouldBeEnabled);

    // button is hidden if the poll is closed OR (IDP is not ready AND user is logged in)
    // Checkmark appears if the poll is closed and this nominee is voted.
    // Anonymous users can always vote, but logged-in users need IDP to be ready
    const buttonShouldBeHidden = pollIsClosed || pollIsWinner || (!isIdpReady && isAuthenticated);
    setIsButtonHidden(buttonShouldBeHidden);
    //eslint-disable-next-line
  }, [user?.alreadyVoted, isIdpReady, pollIsClosed, pollIsWinner, isAuthenticated, isCurrentSlide]);

  useEffect(() => {
    dispatch(actions.user.setUserVoted(poll));
  }, [poll, dispatch]);

  return { isVoted, isButtonDisabled, isButtonHidden };
};
